"use client";

import {
  type ProvenanceActor,
  ProvenanceAvatarStack,
} from "@product/components/app/runs/provenance-avatar-stack";

import { ProductBridge } from "@/components/editorial";

/**
 * GOV-011 · The inbox, as the Twin asks — the governed surface plane as a
 * product moment. Everything the Twin builds arrives as a question in plain
 * words: link two records, use a definition, run a job. The three cards are
 * the product's three proposal kinds (link, definition, action) with the
 * product's three answers (Accept, Decline, Later); the copy carries what
 * the product actually does with each answer. No kind labels, versions,
 * confidences or policy rows: those live in the product's detail pane, not
 * on the page. Illustrative names only — Xero's demo organisation and the
 * board's fictional renewals channel.
 */

const TWIN_ACTOR: ProvenanceActor = {
  label: "MLX",
  imageUrl: null,
  initials: "T",
  icon: "twin",
};

const ASKS = [
  {
    time: "07:02",
    question: "Link these as the same customer?",
    why: "Ridgeway University in Xero, Ridgeway Univ. in HubSpot. Name and web address match; the company numbers don't, so the Twin won't decide this one alone.",
  },
  {
    time: "07:02",
    question: "Use this definition of monthly revenue?",
    why: "Written from your Xero invoices and checked against your own ledger. Every answer about revenue would use it.",
  },
  {
    time: "07:04",
    question: "Refresh the aged receivables report for you?",
    why: "Two people asked this morning. It would rerun after the Xero sync and post the figures in #renewals, as you, with your permissions.",
  },
] as const;

const PRIMARY_CLS =
  "inline-flex h-7 items-center rounded-md bg-[hsl(var(--solid))] px-2.5 text-[12px] font-medium text-[hsl(var(--solid-foreground))]";
const SECONDARY_CLS =
  "inline-flex h-7 items-center rounded-md border border-input bg-background px-2.5 text-[12px] font-medium text-foreground";

export function ProposalInboxExhibit() {
  return (
    <figure className="m-0">
      <ProductBridge className="p-0">
        <div className="min-w-0 overflow-hidden border border-border bg-card">
          <div className="flex items-center justify-between gap-3 border-b border-border/60 px-3.5 py-2.5">
            <p className="m-0 text-[12px] font-semibold text-foreground">Inbox · from the Twin</p>
            <p className="m-0 text-[12px] text-muted-foreground">3 waiting for you</p>
          </div>
          <div className="divide-y divide-border/50">
            {ASKS.map((ask) => (
              <article key={ask.question} className="px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <ProvenanceAvatarStack actor={TWIN_ACTOR} action="created" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-[12px] leading-4">
                      <span className="font-semibold text-foreground">MLX</span>{" "}
                      <span className="text-muted-foreground">· {ask.time}</span>
                    </p>
                    <p className="m-0 mt-1 text-sm leading-snug font-semibold text-foreground">
                      {ask.question}
                    </p>
                    <p className="m-0 mt-1 max-w-[66ch] text-[13px] leading-relaxed text-muted-foreground">
                      {ask.why}
                    </p>
                    <div aria-hidden="true" className="mt-2.5 flex flex-wrap gap-1.5">
                      <span className={PRIMARY_CLS}>Accept</span>
                      <span className={SECONDARY_CLS}>Decline</span>
                      <span className={SECONDARY_CLS}>Later</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="border-t border-border/60 px-3.5 py-2">
            <p className="m-0 text-[12px] leading-relaxed text-muted-foreground">
              Every decision is kept with who made it; a link or a definition can be undone.
            </p>
          </div>
        </div>
      </ProductBridge>
      <figcaption className="mt-2.5 text-[0.72rem] leading-[1.5] text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]">
        Illustrative specimen, names from Xero&rsquo;s demo organisation.
      </figcaption>
    </figure>
  );
}
